import classNames from 'classnames'
import { useEffect, useState } from 'react'
import { Image } from 'src/components/ui/Image'
import { sendEvent, makeEventTrack } from 'src/utils/restructure/analytics'
import type {
  GalleryImageDesktopProps,
  Video,
  Image as TImage,
} from 'src/components/restructure/product/sections/GalleryImage/types/gallery-image'
import './index.scss'

import { GalleryFullView } from '../GalleryFullView'
import { GallerySkeleton } from '../GallerySkeleton'
import { GalleryMovie } from '../GalleryMovie'
import { WishListButton } from '../../Sidebar/ProductTitle/WishListButton'
import { ButtonMorePictures } from '../ButtonMorePictures'
import { getProductBenefits } from '../../GalleryProductBenefits/utils'
import { useProductContextSelector } from '../../../contexts'
import { GalleryProductBenefits } from '../../GalleryProductBenefits'
import { PreSaleFlag } from '../GalleryFlags'

const MAX_IMAGE_SIZE = 1050
const NORMAL_IMAGE_SIZE = 628

const GalleryItem = ({
  item,
  index,
  openGalleryView,
  smallImageThreshold,
  hasOnlyOneImage,
  addIndex,
  priority = false,
  isFullSize = false,
}: {
  item: TImage | Video
  index: number
  openGalleryView: (index: number, event: React.MouseEvent) => void
  smallImageThreshold: number
  hasOnlyOneImage: boolean
  addIndex?: number
  priority: boolean
  isFullSize?: boolean
}) => {
  const isImageSmall = index >= smallImageThreshold
  const fullSize = hasOnlyOneImage || isFullSize

  const imageSize = fullSize ? MAX_IMAGE_SIZE : NORMAL_IMAGE_SIZE

  const itemKey = 'url' in item ? item.url : item.imageUrl

  return (
    <div
      key={`${itemKey}-${index}`}
      className={classNames(
        'min-w-0 bg-restructure-background-secondary',
        'mix-blend-multiply',
        {
          'col-span-2': !hasOnlyOneImage && isImageSmall,
          'col-span-3': !hasOnlyOneImage && !isImageSmall,
          'col-span-6': fullSize,
        }
      )}
    >
      {'url' in item ? (
        <GalleryMovie {...item} />
      ) : (
        <Image
          className="w-full h-full object-cover hover:cursor-zoom-in bg-restructure-background-secondary mix-blend-multiply"
          key={item.imageUrl}
          src={item.imageUrl}
          width={imageSize}
          height={imageSize}
          alt={item.imageText}
          loading={priority ? 'eager' : 'lazy'}
          fetchpriority={priority ? 'high' : 'auto'}
          preload={priority}
          data-testid="image-gallery"
          aria-label="Imagem da galeria"
          onClick={(event) =>
            openGalleryView(addIndex ? index + addIndex : index, event)
          }
        />
      )}
    </div>
  )
}

export const GalleryImageDesktop = ({
  images,
  video,
  isAlbumGrid = false,
  isPresale = false,
}: GalleryImageDesktopProps) => {
  const [isOpenGalleryFullView, setIsOpenGalleryFullView] = useState(false)
  const [selectedImageForClick, setSelectedImageForClick] = useState(0)
  const currentSku = useProductContextSelector((ctx) => ctx.currentSku)
  const { productBenefits } = currentSku

  const productBenefitsArray = getProductBenefits(productBenefits)
  const filteredBenefits = productBenefitsArray.filter(
    (benefit) => benefit.image
  )

  const openGalleryView = (image: number, event: { target: any }) => {
    setIsOpenGalleryFullView(true)
    setSelectedImageForClick(image)

    const eventTrack = makeEventTrack({
      eventAction: event.target.ariaLabel,
      eventPage: 'pagina de produto',
    })

    sendEvent(eventTrack)
  }

  const closeGalleryView = () => {
    setIsOpenGalleryFullView(false)
  }

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeGalleryView()
      }
    }

    if (isOpenGalleryFullView) {
      document.addEventListener('keydown', handleKeyDown)
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpenGalleryFullView])

  const hasOnlyOneImage = images.length === 1 && !video
  const maxViewImages = isAlbumGrid ? 3 : 7

  const itemsGallery: Array<Video | TImage> = video
    ? [...images.slice(0, 1), video, ...images.slice(1)]
    : images

  const galleryContent =
    filteredBenefits.length > 0 && !isAlbumGrid ? (
      <>
        {itemsGallery.slice(0, 2).map((item, index) => (
          <GalleryItem
            key={`${'url' in item ? item.url : item.imageUrl}-${index}`}
            item={item}
            index={index}
            priority
            openGalleryView={openGalleryView}
            smallImageThreshold={2}
            hasOnlyOneImage={hasOnlyOneImage}
          />
        ))}
        <GalleryProductBenefits productBenefits={filteredBenefits} />
        {itemsGallery.slice(2, 7).map((item, index) => (
          <GalleryItem
            key={`${'url' in item ? item.url : item.imageUrl}-${index}`}
            item={item}
            index={index}
            priority={index < 2}
            openGalleryView={openGalleryView}
            smallImageThreshold={2}
            hasOnlyOneImage={hasOnlyOneImage}
            addIndex={2}
          />
        ))}
      </>
    ) : (
      <>
        {itemsGallery.slice(0, maxViewImages).map((item, index) => (
          <GalleryItem
            key={`${'url' in item ? item.url : item.imageUrl}-${index}`}
            item={item}
            index={index}
            priority={index < 4}
            isFullSize={isAlbumGrid && index === 0}
            openGalleryView={openGalleryView}
            smallImageThreshold={4}
            hasOnlyOneImage={hasOnlyOneImage}
          />
        ))}
      </>
    )

  return (
    <section className="gallery-desktop max-w-full overflow-hidden pb-[64px] hidden relative restructure-small-desktop:block">
      {itemsGallery.length ? (
        <div className="relative min-w-0">
          <div className="grid min-w-0 grid-cols-6">{galleryContent}</div>
        </div>
      ) : (
        <GallerySkeleton loading images={images} />
      )}

      {itemsGallery.length > maxViewImages && (
        <div className="absolute bottom-20 right-4 z-10">
          <ButtonMorePictures
            numberOfImages={itemsGallery.length}
            openGalleryView={openGalleryView}
          />
        </div>
      )}

      {isOpenGalleryFullView && (
        <GalleryFullView
          isOpen={isOpenGalleryFullView}
          closeGalleryView={closeGalleryView}
          images={itemsGallery}
          selectedImageForClick={selectedImageForClick}
        />
      )}
      {isPresale && (
        <div className="absolute top-0 left-0 z-10">
          <PreSaleFlag />
        </div>
      )}
      <div className="absolute top-4 right-4 z-10">
        <WishListButton mobileSize="16" desktopSize="16" variant="pdp" />
      </div>
    </section>
  )
}
